import React, { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  MapPin,
  Package,
  Search,
} from "lucide-react";
import "./services/Warehousing.css";
import "./Tracking.css";

const modes = [
  ["mobile", "Mobile"],
  ["awb", "AWB"],
  ["orderId", "Order ID"],
  ["lrn", "LRN"],
];

const faqs = [
  [
    "Where can I find my AWB?",
    "Check the shipment confirmation or label supplied by the sender. Ask the sender if you haven’t received a consignment reference.",
  ],
  [
    "Why can’t I find my shipment?",
    "This demo searches local JPSY records only. Check your selected reference type and the entered value. Contact JPSY for help with a real shipment.",
  ],
  [
    "What if my parcel is delayed?",
    "Review the recorded milestones and contact support with your consignment reference. The dates here reflect saved records and are not a live delivery promise.",
  ],
  [
    "Can I search by mobile number?",
    "The demo matches a 10-digit sender or receiver number in the saved sample records. A production mobile lookup would need authenticated access.",
  ],
  [
    "What should I do about a returned shipment?",
    "Contact the sender or JPSY support with the consignment reference to discuss the recorded status and next steps.",
  ],
];

const statusLabel = (status) => (status || "").replace(/_/g, " ");

export default function Tracking({ shipments, onTabChange }) {
  const [mode, setMode] = useState("awb");
  const [value, setValue] = useState("");
  const [query, setQuery] = useState(null);
  const [error, setError] = useState("");

  const results = query
    ? shipments.filter((shipment) =>
        query.mode === "mobile"
          ? [shipment.senderMobile, shipment.receiverMobile].some(
              (mobile) =>
                String(mobile || "").replace(/\D/g, "") === query.value,
            )
          : String(shipment[query.mode] || "").toLowerCase() ===
            query.value.toLowerCase(),
      )
    : [];

  const navigate = (tab) => {
    onTabChange(tab);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = (event) => {
    event.preventDefault();
    const input = value.trim();

    if (!input) {
      setError("Enter a tracking reference to continue.");
      setQuery(null);
      return;
    }

    if (mode === "mobile" && !/^\d{10}$/.test(input)) {
      setError("Enter a 10-digit mobile number.");
      setQuery(null);
      return;
    }

    setError("");
    setQuery({ mode, value: input });
  };

  const selectMode = (nextMode) => {
    setMode(nextMode);
    setValue("");
    setError("");
    setQuery(null);
  };

  const showSampleShipment = () => {
    const sampleAwb = "JPSY10012026";
    setMode("awb");
    setValue(sampleAwb);
    setError("");
    setQuery({ mode: "awb", value: sampleAwb });
  };

  return (
    <div className="wh-page tracking-page">
      <section className="tracking-hero">
        <div className="wh-wrap">
          <div className="tracking-heading">
            <h1>
              Your parcel. <span>Its next chapter.</span>
            </h1>
            <p>
              Track your parcel and follow its journey through recorded
              shipment updates.
            </p>
          </div>
          <div className="tracking-grid">
            <div className="tracking-intro">
              <img
                src={`${process.env.PUBLIC_URL}/assets/delivery-partner/doorstep.png`}
                alt="JPSY parcel handover at a customer’s doorstep"
              />
            </div>

            <div className="tracking-card">
              <h2>Track your shipment</h2>
              <p>Enter your shipment details to see the latest updates.</p>

              <div
                className="tracking-modes"
                role="group"
                aria-label="Tracking reference type"
              >
                {modes.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={mode === id}
                    onClick={() => selectMode(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={submit}>
                <label htmlFor="tracking-reference">
                  {modes.find(([id]) => id === mode)[1]}{" "}
                  {mode === "mobile" ? "number" : "reference"}
                </label>
                <div className="tracking-input-row">
                  <input
                    id="tracking-reference"
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    inputMode={mode === "mobile" ? "numeric" : "text"}
                    autoComplete="off"
                    placeholder={
                      mode === "awb"
                        ? "e.g. JPSY10012026"
                        : `Enter ${modes.find(([id]) => id === mode)[1]}`
                    }
                    aria-invalid={Boolean(error)}
                    aria-describedby="tracking-feedback"
                  />
                  <button className="tracking-submit" type="submit">
                    <Search size={18} aria-hidden="true" />
                    Track
                  </button>
                </div>
                <p id="tracking-feedback" role="alert">
                  {error}
                </p>
              </form>

              <div className="tracking-demo">
                <strong>Demo tracking</strong>
                <p>
                  This page searches shipment records saved in this browser.
                  Live carrier tracking is not connected.
                </p>
                <button type="button" onClick={showSampleShipment}>
                  Try a sample shipment
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {query && (
        <section
          className="wh-wrap wh-section tracking-results"
          aria-live="polite"
        >
          <div className="wh-heading-row">
            <h2>Shipment updates</h2>
            <p>
              {results.length} matching{" "}
              {results.length === 1 ? "shipment" : "shipments"}
            </p>
          </div>
          {!results.length ? (
            <div className="tracking-empty">
              <Package size={30} />
              <h3>No matching shipment found.</h3>
              <p>
                Check the reference and try again. These results cover only the
                saved JPSY records in this browser.
              </p>
            </div>
          ) : (
            results.map((shipment) => (
              <article className="tracking-result" key={shipment.awb}>
                <header>
                  <div>
                    <span>CONSIGNMENT</span>
                    <h3>{shipment.awb}</h3>
                    <p>{shipment.serviceType}</p>
                  </div>
                  <span className="tracking-status">
                    {statusLabel(shipment.status)}
                  </span>
                </header>
                <div className="tracking-route">
                  <span>
                    <MapPin size={18} />
                    {shipment.originCity}
                  </span>
                  <ArrowRight size={20} />
                  <span>
                    <MapPin size={18} />
                    {shipment.destinationCity}
                  </span>
                </div>
                <p className="tracking-estimate">
                  Recorded estimated delivery:{" "}
                  {shipment.estimatedDelivery || "Not available"}
                </p>
                <ol>
                  {(shipment.milestones || []).map((milestone, index) => (
                    <li
                      key={`${milestone.title}-${index}`}
                      className={milestone.completed ? "is-complete" : ""}
                    >
                      {milestone.completed ? (
                        <CheckCircle2 size={20} />
                      ) : (
                        <Circle size={20} />
                      )}
                      <div>
                        <h4>{milestone.title}</h4>
                        <p>
                          {milestone.location}
                          {milestone.timestamp
                            ? ` · ${milestone.timestamp}`
                            : ""}
                        </p>
                        <p>{milestone.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </article>
            ))
          )}
        </section>
      )}

      <section className="tracking-info">
        <div className="wh-wrap tracking-info-grid">
          <aside className="tracking-feature">
            <div className="tracking-feature-icon">
              <Package size={26} />
            </div>
            <div className="wh-eyebrow">SHIPPING WITH JPSY</div>
            <h2>How can we help with your next shipment?</h2>
            <p>
              Explore delivery options for personal parcels or business
              shipments.
            </p>
            <button
              className="wh-text-button"
              onClick={() => navigate("solution_personal_courier")}
            >
              Personal Courier <ArrowRight size={17} />
            </button>
            <button
              className="wh-text-button"
              onClick={() => navigate("solution_d2c_brands")}
            >
              Business shipping <ArrowRight size={17} />
            </button>
          </aside>

          <div className="tracking-faq">
            <div>
              <div className="wh-eyebrow">HELP WITH THE JOURNEY</div>
              <h2>Tracking questions, answered.</h2>
              <button
                className="wh-text-button"
                onClick={() => navigate("support")}
              >
                Contact support <ArrowRight size={17} />
              </button>
            </div>
            <div>
              {faqs.map(([question, answer]) => (
                <details key={question}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
